import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import { FrontendEvent } from '@/services/transform';
import { EventType } from '@/mocks/events';
import { eventDetails, AgendaItemType } from '@/mocks/eventDetails';
import CaptchaInput from '@/components/base/CaptchaInput';
import { getAdminEvents, getRegistrations } from '@/utils/dataStore';
import { getPublicEventAgendas, type PublicAgenda } from '@/services/publicAgenda.api';
import { getPublicEventGuests, type EventGuest } from '@/services/publicGuests.api';
import PhotoGallery from '@/components/feature/PhotoGallery';
import EventRegistrationForm from './EventRegistrationForm';

const TYPE_COLORS: Record<EventType, string> = {
  offline: 'bg-emerald-100 text-emerald-700',
  online: 'bg-sky-100 text-sky-700',
  hybrid: 'bg-amber-100 text-amber-700',
};

const AGENDA_TYPE_STYLES: Record<AgendaItemType, { cls: string; icon: string }> = {
  keynote: { cls: 'bg-[#0A2540] text-white', icon: 'ri-mic-line' },
  panel: { cls: 'bg-[#2C7DA0]/20 text-[#2C7DA0]', icon: 'ri-group-line' },
  workshop: { cls: 'bg-amber-100 text-amber-700', icon: 'ri-tools-line' },
  break: { cls: 'bg-gray-100 text-gray-500', icon: 'ri-cup-line' },
  ceremony: { cls: 'bg-purple-100 text-purple-700', icon: 'ri-star-line' },
};

type Tab = 'overview' | 'agenda' | 'speakers' | 'register';

const MONTH_LONG_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function parseLocalDate(s: string): Date {
  // YYYY-MM-DD 视为纽约时区的纯日期，避免 UTC 解析导致显示日期偏移
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function formatFullDate(dateStr: string, endDateStr: string, lang: string): string {
  const start = parseLocalDate(dateStr);
  const end = parseLocalDate(endDateStr);
  if (lang === 'zh') {
    return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 – ${end.getDate()}日 (纽约时间)`;
  }
  return `${MONTH_LONG_EN[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()} (ET)`;
}

function formatICSDate(dateStr: string): string {
  // Returns YYYYMMDD format for all-day .ics event
  return dateStr.replace(/-/g, '');
}

function generateICS(event: GtraEvent, isZh: boolean): string {
  const title = isZh ? event.title : event.titleEn;
  const description = (isZh ? event.description : event.descriptionEn).replace(/\n/g, '\\n').replace(/,/g, '\\,');
  const location = isZh ? event.location : event.locationEn;
  const startDate = formatICSDate(event.date);
  // End date for all-day event should be day after endDate
  const endDateObj = new Date(event.endDate);
  endDateObj.setDate(endDateObj.getDate() + 1);
  const endDate = endDateObj.toISOString().split('T')[0].replace(/-/g, '');
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const url = `${window.location.origin}/events?id=${event.id}`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GTRA//GTRA Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:gtra-${event.id}@gtraweb3.com`,
    `DTSTAMP:${now}`,
    `DTSTART;VALUE=DATE:${startDate}`,
    `DTEND;VALUE=DATE:${endDate}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    `URL:${url}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

function handleAddToCalendar(event: FrontendEvent, isZh: boolean) {
  const icsContent = generateICS(event, isZh);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `GTRA_${event.id}.ics`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ShareDropdown({
  event,
  isZh,
  onShowPoster,
  onClose,
}: {
  event: FrontendEvent;
  isZh: boolean;
  onShowPoster: () => void;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/events?id=${event.id}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); onClose(); }, 1500);
    }).catch(() => {
      // Fallback for environments without clipboard API
      const ta = document.createElement('textarea');
      ta.value = `${window.location.origin}/events?id=${event.id}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => { setCopied(false); onClose(); }, 1500);
    });
  };

  return (
    <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-xl overflow-hidden border border-[#0A2540]/10 z-20"
      style={{ boxShadow: '0 8px 24px rgba(10,37,64,0.12)' }}
    >
      <button
        onClick={handleCopyLink}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-xs text-[#0A2540] hover:bg-[#F7F9FC] transition-colors cursor-pointer whitespace-nowrap"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={copied ? 'ri-check-line text-emerald-600' : 'ri-links-line text-[#2C7DA0]'} />
        </div>
        {copied
          ? (isZh ? '链接已复制！' : 'Link Copied!')
          : (isZh ? '复制分享链接' : 'Copy Link')
        }
      </button>
      <div className="h-px bg-[#0A2540]/6 mx-3" />
      <button
        onClick={() => { onShowPoster(); onClose(); }}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-xs text-[#0A2540] hover:bg-[#F7F9FC] transition-colors cursor-pointer whitespace-nowrap"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-image-line text-[#E8A838]" />
        </div>
        {isZh ? '生成活动海报' : 'Preview Poster'}
      </button>
    </div>
  );
}

function PosterModal({ event, isZh, onClose }: { event: FrontendEvent; isZh: boolean; onClose: () => void }) {
  const [agendas, setAgendas] = useState<PublicAgenda[]>([]);
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const posterRef = useRef<HTMLDivElement>(null);
  
  const galleryImages = event.gallery && event.gallery.length > 0 ? event.gallery.slice(0, 6) : [];
  const hasGallery = galleryImages.length > 0;
  
  // 生成活动详情链接
  const eventDetailUrl = `https://gtraweb3.com/events/${event.id}`;
  
  // 把图片转成 dataURL，避免 html2canvas 跨域污染 canvas
  const inlineImagesAsDataURL = async (root: HTMLElement) => {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      try {
        // 把图片 URL 规整到当前 origin，避免 www / 非 www 跨域
        const u = new URL(src, window.location.href);
        u.protocol = window.location.protocol;
        u.host = window.location.host;
        const absUrl = u.href;
        const res = await fetch(absUrl, { credentials: 'omit' });
        if (!res.ok) {
          console.warn('图片获取失败:', absUrl, res.status);
          return;
        }
        const blob = await res.blob();
        const dataUrl: string = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        img.src = dataUrl;
        if (typeof img.decode === 'function') {
          try { await img.decode(); } catch { /* ignore */ }
        } else {
          await new Promise<void>((resolve) => {
            if (img.complete) resolve();
            else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          });
        }
      } catch (e) {
        console.warn('图片内联失败:', src, e);
      }
    }));
  };

  // 下载海报图片
  const toDataURL = async (url: string): Promise<string> => {
  // 把图片 URL 规整到当前 origin，避免 www/非 www 跨域
  const u = new URL(url, window.location.href);
  u.protocol = window.location.protocol;
  u.host = window.location.host;

  const response = await fetch(u.href, {
    credentials: 'omit',
  });

  const blob = await response.blob();

  return await new Promise((resolve) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result as string);
    };

    reader.readAsDataURL(blob);
  });
};
const inlineAllImages = async (root: HTMLElement) => {
  // 普通 img
  const imgs = Array.from(root.querySelectorAll('img'));

  await Promise.all(
    imgs.map(async (img) => {
      const src = img.src;

      if (
        !src ||
        src.startsWith('data:')
      ) {
        return;
      }

      try {
        const dataUrl = await toDataURL(src);

        img.src = dataUrl;
      } catch (err) {
        console.warn('img inline failed:', src);
      }
    }),
  );

  // background-image
  const all = Array.from(root.querySelectorAll('*'));

  await Promise.all(
    all.map(async (el) => {
      const htmlEl = el as HTMLElement;

      const bg =
        window.getComputedStyle(htmlEl)
          .backgroundImage;

      if (
        !bg ||
        bg === 'none'
      ) {
        return;
      }

      const match = bg.match(
        /url\(["']?(.*?)["']?\)/,
      );

      if (!match?.[1]) {
        return;
      }

      const url = match[1];

      if (url.startsWith('data:')) {
        return;
      }

      try {
        const dataUrl =
          await toDataURL(url);

        htmlEl.style.backgroundImage =
          `url("${dataUrl}")`;
      } catch (err) {
        console.warn(
          'background inline failed:',
          url,
        );
      }
    }),
  );
};

const downloadPoster = async () => {
  if (isDownloading || !posterRef.current) return;

  setIsDownloading(true);

  try {
    const element = posterRef.current;

    await document.fonts.ready;

    // 关键：转 base64
    await inlineAllImages(element);

    // 等待渲染稳定
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    const rect =
      element.getBoundingClientRect();

    const canvas = await html2canvas(
      element,
      {
        useCORS: true,
        allowTaint: false,

        backgroundColor: '#0A2540',

        logging: false,

        scale: 2,

        width: rect.width,
        height: rect.height,

        scrollX: -window.scrollX,
        scrollY: -window.scrollY,

        windowWidth:
          document.documentElement.clientWidth,

        windowHeight:
          document.documentElement.clientHeight,
      },
    );

    const link =
      document.createElement('a');

    link.download = `${
      isZh
        ? event.title
        : event.titleEn
    }_poster.png`;

    link.href =
      canvas.toDataURL('image/png');

    link.click();
  } catch (err) {
    console.error(err);

    alert(
      isZh
        ? '下载失败，请重试'
        : 'Download failed, please try again',
    );
  } finally {
    setIsDownloading(false);
  }
};

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // 获取议程和嘉宾数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [agendasData, guestsData] = await Promise.all([
          getPublicEventAgendas(event.id),
          getPublicEventGuests(event.id)
        ]);
        setAgendas(agendasData); // 获取所有议程
        setGuests(guestsData.slice(0, 4)); // 只显示前4位嘉宾
      } catch (error) {
        console.error('获取海报数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [event.id]);

  const dateStr = (() => {
    const start = parseLocalDate(event.date);
    const end = parseLocalDate(event.endDate);
    if (isZh) {
      return `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 – ${end.getDate()}日 (纽约时间)`;
    }
    return `${MONTH_LONG_EN[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()} (ET)`;
  })();

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl mx-auto mt-2 mb-8 px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-[#0A2540] hover:bg-white transition-colors cursor-pointer"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
        >
          <i className="ri-close-line text-sm" />
        </button>

        {/* Poster Card */}
        <div
          ref={posterRef}
          className="rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.4)', backgroundColor: '#0A2540' }}
        >
          {/* Cover Image */}
          <div className="relative h-[23rem]">
            <div
              style={{
                backgroundImage: `url(${event.cover})`,
                backgroundSize: 'cover',
                backgroundPosition: 'top center',
              }}
              className="w-full h-full"
              role="img"
              aria-label={isZh ? event.title : event.titleEn}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/40 via-transparent to-[#0A2540]/90" />
            {/* GTRA Badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <div className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20">
                <span className="text-white font-bold text-xs tracking-widest">GTRA</span>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  event.type === 'offline' ? 'bg-emerald-500/80' : event.type === 'online' ? 'bg-sky-500/80' : 'bg-amber-500/80'
                } text-white backdrop-blur-sm`}
              >
                {isZh
                  ? event.type === 'offline' ? '线下' : event.type === 'online' ? '线上' : '混合'
                  : event.type === 'offline' ? 'In-Person' : event.type === 'online' ? 'Online' : 'Hybrid'
                }
              </span>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-white font-bold text-xl leading-snug whitespace-normal">
                {isZh ? event.title : event.titleEn}
              </h2>
            </div>
          </div>

          {/* Poster Body */}
          <div className="bg-[#0A2540] px-4 py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-calendar-line text-[#E8A838]" />
                </div>
                <span>{dateStr}</span>
              </div>
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#E8A838]" />
                </div>
                <span>{isZh ? event.location : event.locationEn}</span>
              </div>
            </div>

            <p className="text-white/55 text-xs leading-relaxed mb-3 whitespace-normal">
              {isZh ? event.description : event.descriptionEn}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {(isZh ? event.tags : event.tagsEn).slice(0, 4).map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px]">
                  {tag}
                </span>
              ))}
            </div>

            {/* 议程概览 */}
            {!loading && agendas.length > 0 && (
              <div className="mb-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-time-line text-[#E8A838] text-xs" />
                  <span className="text-white/60 text-[10px] font-medium">
                    {isZh ? '精彩议程 (纽约时间)' : 'Highlights (ET)'}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {agendas.slice(0, 8).map((agenda) => (
                    <div key={agenda.id} className="flex items-start gap-2">
                      <span className="text-[#E8A838] text-[10px] font-mono mt-0.5">{agenda.time}</span>
                      <span className="text-white/80 text-[10px] flex-1 leading-tight">
                        {isZh ? agenda.title : (agenda.title_en || agenda.title)}
                      </span>
                      {agenda.speaker_name && (
                        <span className="text-white/50 text-[9px] whitespace-nowrap mt-0.5">
                          {agenda.speaker_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 嘉宾展示 */}
            {!loading && guests.length > 0 && (
              <div className="mb-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-user-star-line text-[#E8A838] text-xs" />
                  <span className="text-white/60 text-[10px] font-medium">
                    {isZh ? '重磅嘉宾' : 'Featured Speakers'}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {guests.slice(0, 4).map((guest) => (
                    <div key={guest.id} className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 bg-white/10">
                        {guest.guest?.photo ? (
                          <div
                            style={{
                              backgroundImage: `url(${guest.guest.photo})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'top center',
                            }}
                            className="w-full h-full"
                            role="img"
                            aria-label={guest.guest?.name}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/40">
                            <i className="ri-user-line text-2xl" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-white text-xs leading-tight">
                          {isZh
                            ? (guest.guest?.name || guest.speaker_name || '嘉宾')
                            : (guest.guest?.name_en || guest.guest?.name || 'Guest')
                          }
                        </h4>
                        <p className="text-[#E8A838] text-[10px] font-medium mt-0.5 line-clamp-1">
                          {isZh
                            ? (guest.guest?.title || guest.speaker_title || '')
                            : (guest.guest?.title_en || guest.guest?.title || '')
                          }
                        </p>
                        <p className="text-white/50 text-[10px] mt-0.5 line-clamp-1">
                          {isZh
                            ? (guest.guest?.institution || '')
                            : (guest.guest?.institution_en || guest.guest?.institution || '')
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 相册展示（仅历史活动有相册时显示） */}
            {hasGallery && (
              <div className="mb-3 pt-3 border-t border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-gallery-line text-[#E8A838] text-xs" />
                  <span className="text-white/60 text-[10px] font-medium">
                    {isZh ? '活动相册' : 'Event Gallery'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {galleryImages.map((imgUrl, index) => (
                    <div 
                      key={index} 
                      className="aspect-square rounded-lg overflow-hidden bg-white/10"
                    >
                      <div
                        style={{
                          backgroundImage: `url(${imgUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                        className="w-full h-full"
                        role="img"
                        aria-label={`${isZh ? event.title : event.titleEn} - Photo ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <div>
                <div className="text-white/35 text-[10px] mb-0.5">{isZh ? '全球科创圆桌联盟' : 'Global Tech Roundtable Alliance'}</div>
                <div className="text-white/60 text-[10px]">gtraweb3.com</div>
              </div>
              <div className="text-right">
                <div className="flex flex-col items-end gap-1">
                  <div className="bg-white rounded-lg p-1">
                    <QRCodeSVG
                      value={eventDetailUrl}
                      size={52}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <span className="text-white/60 text-[9px]">
                    {isZh ? '扫码了解详情' : 'Learn More'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-3">
          <button
            onClick={downloadPoster}
            disabled={isDownloading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-medium transition-all hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #2C7DA0, #3A8FB7)' }}
          >
            {isDownloading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                {isZh ? '生成中...' : 'Generating...'}
              </>
            ) : (
              <>
                <i className="ri-download-line text-sm" />
                {isZh ? '下载海报' : 'Download Poster'}
              </>
            )}
          </button>
          <p className="text-white/40 text-xs mt-2">
            {isZh ? '长按或截图也可保存海报' : 'Long press or screenshot to save poster'}
          </p>
        </div>
      </div>
    </div>
  );
}

interface Props {
  event: FrontendEvent;
  onClose: () => void;
  defaultTab?: Tab;
}

export default function EventDetailModal({ event, onClose, defaultTab = 'overview' }: Props) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);
  
  // 议程数据
  const [agendas, setAgendas] = useState<PublicAgenda[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  
  // 嘉宾数据
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [guestLoading, setGuestLoading] = useState(false);

  const isZh = i18n.language === 'zh';
  const detail = eventDetails.find((d) => d.eventId === event.id);

  // Capacity check
  const adminEvent = getAdminEvents().find((e) => e.id === event.id);
  const registrations = getRegistrations();
  const approvedCount = registrations.filter((r) => r.eventId === event.id && r.status === 'approved').length;
  const maxCapacity = adminEvent?.maxCapacity;
  const isAtCapacity = maxCapacity != null && approvedCount >= maxCapacity;
  
  // 加载嘉宾数据
  useEffect(() => {
    const loadGuests = async () => {
      setGuestLoading(true);
      try {
        const eventId = parseInt(event.id);
        if (!isNaN(eventId) && eventId > 0) {
          const data = await getPublicEventGuests(eventId);
          setGuests(data);
        }
      } catch (error) {
        console.error('加载嘉宾数据失败:', error);
      } finally {
        setGuestLoading(false);
      }
    };
    
    // 只在切换到嘉宾Tab或默认显示嘉宾Tab时加载
    if (activeTab === 'speakers' || defaultTab === 'speakers') {
      loadGuests();
    }
  }, [event.id, activeTab, defaultTab]);
  
  // 加载议程数据
  useEffect(() => {
    const loadAgendas = async () => {
      setAgendaLoading(true);
      try {
        // 从事件ID提取数字部分
        const eventId = parseInt(event.id);
        if (!isNaN(eventId) && eventId > 0) {
          const data = await getPublicEventAgendas(eventId);
          setAgendas(data);
        }
      } catch (error) {
        console.error('加载议程失败:', error);
      } finally {
        setAgendaLoading(false);
      }
    };
    
    // 只在切换到议程Tab或默认显示议程Tab时加载
    if (activeTab === 'agenda' || defaultTab === 'agenda') {
      loadAgendas();
    }
  }, [event.id, activeTab, defaultTab]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  // Close share menu when clicking outside
  useEffect(() => {
    if (!showShareMenu) return;
    const handler = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showShareMenu]);

  const tabs: { key: Tab; i18nKey: string; icon: string }[] = [
    { key: 'overview', i18nKey: 'eventModal.tab.overview', icon: 'ri-information-line' },
    { key: 'agenda', i18nKey: 'eventModal.tab.agenda', icon: 'ri-list-ordered' },
    { key: 'speakers', i18nKey: 'eventModal.tab.speakers', icon: 'ri-user-star-line' },
    { key: 'register', i18nKey: 'eventModal.tab.register', icon: 'ri-edit-line' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden rounded-t-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative">
          <div className="w-full h-36 overflow-hidden">
            <img
              src={event.cover}
              alt={isZh ? event.title : event.titleEn}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 via-[#0A2540]/30 to-transparent" />
          </div>
          {/* Action buttons row - close + share + calendar */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {/* Add to Calendar button */}
            <button
              onClick={() => handleAddToCalendar(event, isZh)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors cursor-pointer"
              title={isZh ? '加入日历' : 'Add to Calendar'}
            >
              <i className="ri-calendar-check-line text-sm" />
            </button>
            {/* Share button */}
            <div className="relative" ref={shareRef}>
              <button
                onClick={() => setShowShareMenu((v) => !v)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors cursor-pointer"
                title={isZh ? '分享' : 'Share'}
              >
                <i className="ri-share-line text-sm" />
              </button>
              {showShareMenu && (
                <ShareDropdown
                  event={event}
                  isZh={isZh}
                  onShowPoster={() => setShowPoster(true)}
                  onClose={() => setShowShareMenu(false)}
                />
              )}
            </div>
            {/* Close button */}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="absolute bottom-3 left-4 right-24">
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-1 ${TYPE_COLORS[event.type]}`}>
              {t(`event.type.${event.type}`)}
            </span>
            <h2 className="text-white font-bold text-lg leading-tight line-clamp-2">
              {isZh ? event.title : event.titleEn}
            </h2>
          </div>
        </div>

        {/* Meta row */}
        <div className="px-5 py-3 flex flex-wrap gap-3 border-b border-[#0A2540]/10 bg-[#F7F9FC]">
          <div className="flex items-center gap-1.5 text-[#0A2540]/60 text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-[#2C7DA0]" />
            </div>
            <span>{formatFullDate(event.date, event.endDate, isZh ? 'zh' : 'en')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[#0A2540]/60 text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-[#2C7DA0]" />
            </div>
            <span>{isZh ? event.location : event.locationEn}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 border-b border-[#0A2540]/10 px-2 overflow-x-auto scrollbar-hide">
          {tabs.map(({ key, i18nKey, icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all cursor-pointer
                ${activeTab === key
                  ? 'border-[#2C7DA0] text-[#2C7DA0]'
                  : 'border-transparent text-[#0A2540]/50 hover:text-[#0A2540]'
                }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${icon}`} />
              </div>
              {t(i18nKey)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <p className="text-[#0A2540]/70 text-sm leading-relaxed">
                {isZh ? event.description : event.descriptionEn}
              </p>
              <div className="flex flex-wrap gap-2">
                {(isZh ? event.tags : event.tagsEn).map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs rounded-full font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              
              {/* Photo Gallery */}
              {event.gallery && event.gallery.length > 0 && (
                <PhotoGallery photos={event.gallery} />
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F7F9FC] rounded-xl p-4">
                  <div className="text-[#0A2540]/40 text-xs mb-1">{isZh ? '活动时间' : 'Event Date'}</div>
                  <div className="text-[#0A2540] text-sm font-medium">
                    {formatFullDate(event.date, event.endDate, isZh ? 'zh' : 'en')}
                  </div>
                </div>
                <div className="bg-[#F7F9FC] rounded-xl p-4">
                  <div className="text-[#0A2540]/40 text-xs mb-1">{isZh ? '地点' : 'Location'}</div>
                  <div className="text-[#0A2540] text-sm font-medium">
                    {isZh ? event.location : event.locationEn}
                  </div>
                </div>
              </div>
              {/* Share + Calendar + Register row */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowShareMenu((v) => !v)}
                  className="flex-shrink-0 px-4 py-3 border border-[#0A2540]/15 text-[#0A2540]/70 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap hover:border-[#0A2540]/30 hover:text-[#0A2540] flex items-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-line" />
                  </div>
                  {isZh ? '分享' : 'Share'}
                </button>
                <button
                  onClick={() => handleAddToCalendar(event, isZh)}
                  className="flex-shrink-0 px-4 py-3 border border-[#0A2540]/15 text-[#0A2540]/70 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap hover:border-[#2C7DA0]/40 hover:text-[#2C7DA0] flex items-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-check-line" />
                  </div>
                  {isZh ? '加入日历' : 'Add to Calendar'}
                </button>
                <button
                  onClick={() => setActiveTab('register')}
                  className="flex-1 py-3 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('eventsPage.register')}
                </button>
              </div>
            </div>
          )}

          {/* Agenda Tab */}
          {activeTab === 'agenda' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-[#0A2540]/50 mb-1">
                <i className="ri-time-zone-line" />
                <span>
                  {isZh ? '所有时间均为纽约时间 (ET)' : 'All times shown in New York Time (ET)'}
                </span>
              </div>
              {agendaLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#2C7DA0] border-t-transparent"></div>
                </div>
              ) : agendas.length > 0 ? (
                // 显示从后端获取的议程数据
                agendas
                  .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                  .map((item, index) => (
                    <div key={item.id || index} className="flex gap-3">
                      <div className="text-right shrink-0 w-24 pt-1">
                        <span className="text-[#0A2540]/40 text-xs font-mono">{item.time}</span>
                        {item.end_time && (
                          <span className="text-[#0A2540]/30 text-[10px] font-mono block">~ {item.end_time}</span>
                        )}
                      </div>
                      <div className="relative pl-4 border-l-2 border-[#0A2540]/10 flex-1 pb-4">
                        <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#2C7DA0]" />
                        {item.location && (
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-[#2C7DA0]/10 text-[#2C7DA0] mb-1">
                            <i className="ri-map-pin-line mr-0.5" />
                            {isZh ? item.location : (item.location_en || item.location)}
                          </span>
                        )}
                        <h4 className="text-[#0A2540] text-sm font-semibold mb-0.5">
                          {isZh ? item.title : (item.title_en || item.title)}
                        </h4>
                        {(item.speaker_name || item.speaker_title || item.speaker_institution) && (
                          <div className="flex items-center gap-1 text-[#2C7DA0] text-xs mb-1">
                            <i className="ri-user-line" />
                            <span>{item.speaker_name}</span>
                            {item.speaker_title && <span className="text-[#0A2540]/50">· {item.speaker_title}</span>}
                          </div>
                        )}
                        {(item.description || item.description_en) && (
                          <p className="text-[#0A2540]/55 text-xs leading-relaxed">
                            {isZh ? item.description : (item.description_en || item.description)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
              ) : detail && detail.agenda.length > 0 ? (
                // 回退：使用本地mock数据
                detail.agenda.map((item) => {
                  const style = AGENDA_TYPE_STYLES[item.type];
                  return (
                    <div key={item.id} className="flex gap-3">
                      <div className="text-right shrink-0 w-24 pt-1">
                        <span className="text-[#0A2540]/40 text-xs font-mono">{item.time}</span>
                      </div>
                      <div className="relative pl-4 border-l-2 border-[#0A2540]/10 flex-1 pb-4">
                        <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#2C7DA0]" />
                        <div className="flex items-start gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${style.cls}`}>
                            <i className={`${style.icon} mr-1`} />
                            {t(`eventModal.agenda.type.${item.type}`)}
                          </span>
                        </div>
                        <h4 className="text-[#0A2540] text-sm font-semibold mb-0.5">
                          {isZh ? item.titleZh : item.titleEn}
                        </h4>
                        {(item.speakerZh || item.speakerEn) && (
                          <div className="flex items-center gap-1 text-[#2C7DA0] text-xs mb-1">
                            <i className="ri-user-line" />
                            <span>{isZh ? item.speakerZh : item.speakerEn}</span>
                          </div>
                        )}
                        {(item.descZh || item.descEn) && (
                          <p className="text-[#0A2540]/55 text-xs leading-relaxed">
                            {isZh ? item.descZh : item.descEn}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-[#0A2540]/40 text-sm">
                  {isZh ? '议程信息即将公布，敬请期待' : 'Agenda will be announced soon, stay tuned'}
                </div>
              )}
            </div>
          )}

          {/* Speakers Tab */}
          {activeTab === 'speakers' && (
            <div>
              {!guestLoading && guests.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {guests.map((guest) => (
                    <div key={guest.id} className="flex items-start gap-3 bg-[#F7F9FC] rounded-xl p-4">
                      <div className="w-24 h-24 rounded-full overflow-hidden shrink-0">
                        {guest.guest?.photo ? (
                          <img
                            src={guest.guest.photo}
                            alt={isZh ? (guest.guest.name || '嘉宾') : (guest.guest.name_en || guest.guest.name || 'Guest')}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#2C7DA0]/20 flex items-center justify-center">
                            <i className="ri-user-line text-2xl text-[#2C7DA0]" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-[#0A2540] text-sm">
                          {isZh
                            ? (guest.guest?.name || guest.speaker_name || '嘉宾')
                            : (guest.guest?.name_en || guest.guest?.name || 'Guest')
                          }
                        </h4>
                        <p className="text-[#2C7DA0] text-xs font-medium mt-0.5">
                          {isZh
                            ? (guest.guest?.title || guest.speaker_title || '')
                            : (guest.guest?.title_en || guest.guest?.title || '')
                          }
                        </p>
                        <p className="text-[#0A2540]/50 text-xs mt-0.5">
                          {isZh
                            ? (guest.guest?.institution || '')
                            : (guest.guest?.institution_en || guest.guest?.institution || '')
                          }
                        </p>
                        {guest.role && (
                          <span className="inline-block mt-1.5 px-2 py-0.5 bg-[#E8A838]/20 text-[#E8A838] text-[10px] rounded-full">
                            {isZh
                              ? (guest.role === 'keynote' ? '主旨演讲' : guest.role === 'panel' ? '圆桌讨论' : guest.role)
                              : guest.role_en || guest.role
                            }
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : guestLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#2C7DA0] border-t-transparent"></div>
                </div>
              ) : (
                <div className="text-center py-8 text-[#0A2540]/40 text-sm">
                  {isZh ? '嘉宾名单即将公布，敬请期待' : 'Speaker list will be announced soon, stay tuned'}
                </div>
              )}
            </div>
          )}

          {/* Register Tab */}
          {activeTab === 'register' && (
            <div>
              {/* 使用新的报名表单组件 */}
              <EventRegistrationForm
                event={event}
                isZh={isZh}
                onSuccess={() => {
                  setSubmitted(true);
                }}
                onError={(error) => {
                  console.error('Registration error:', error);
                }}
                onClose={() => onClose()}
              />
            </div>
          )}
        </div>
      </div>

      {showPoster && (
        <PosterModal event={event} isZh={isZh} onClose={() => setShowPoster(false)} />
      )}
    </div>
  );
}
